import type { RelatedItemsHost } from './atoms';

// Related Items — everything linked to a host record; one row per link, shape set
// by `kind`. Backend renders camelCase; `details` is a per-type snapshot echoed verbatim.

// Placeholder shown wherever a Related Items value is empty or missing.
export const EMPTY = '—';

export type RelatedItemKind =
  | 'note'
  | 'uploaded_file'
  | 'external_link'
  | 'external_title_support'
  | 'vesting_document'
  | 'afe'
  | 'division_order'
  | 'royalty_check'
  | 'courthouse_document'
  | 'regulatory_document'
  | 'well';

// Only record links carry a backend-written `details` snapshot; the accordion's
// labels for those keys live in record-kind-configs.ts.

export type RelatedItemDetails = Record<string, unknown>;

/** One row in the Related Items table. */
export interface RelatedItem {
  id: number;
  kind: RelatedItemKind;
  /** This row's own path: `{basePath}/related_items/:id`, which is not always the
   * section's host — see {@link RelatedItem.origin}. */
  href?: string;
  /**
   * Which record owns the row, for a host whose list reads through to another's items
   * (a Well Workspace staged well overlays its canonical well's). Only that host sends
   * it; absent means the row belongs to the section's own host and is editable.
   *
   * A `canonical` row is read-only here: the backend's read scope covers the list, but
   * update / destroy / download still resolve against the host's own items, so acting
   * on one through this section would 404.
   */
  origin?: 'staged' | 'canonical';
  /** Snapshotted display title (per the standardized per-type title formats). */
  title?: string | null;
  /** Note text, or a note attached to an uploaded file. */
  body?: string | null;
  /** External link URL (or the optional external link on a linked record). */
  url?: string | null;
  /** External link display label. */
  label?: string | null;
  /** Record link's target record; null or absent for files, notes, and links. */
  target?: { type: string; id: string } | null;
  /** Snapshotted per-type detail fields for the accordion. */
  details?: RelatedItemDetails | null;
  /** Present for uploaded files (null otherwise). */
  fileName?: string | null;
  /** Blob-derived file type for uploaded files: extension, else MIME type. */
  fileType?: string | null;
  /** Inline download link for uploaded files (null otherwise). */
  attachmentHref?: string | null;
  byteSize?: number | null;
  /** Backend-owned: true for uploaded files with a blob and record links that
   * snapshotted a file. Drives the row's Download action. */
  downloadable?: boolean;
  createdBy: string | null;
  createdAt: string | null;
  // Human-recorded coverage; NULL when nobody answered. `details.ownership` may be derived.
  coversOwnership?: boolean | null;
  /** A workbook whose sheets are still being extracted, or whose extraction failed. */
  processingStatus?: 'processing' | 'failed' | null;
}

// Record kinds that can be uploaded + provisioned from the panel, mapped to the
// document_type they provision as. `well` is absent — it links only to an existing
// record. Mirrors RelatedItem::RECORD_KIND_DOCUMENT_TYPES on the backend.
export const PROVISIONABLE_KIND_DOCUMENT_TYPES: Partial<
  Record<RelatedItemKind, string>
> = {
  afe: 'afe',
  division_order: 'division_order',
  royalty_check: 'royalty_check',
  courthouse_document: 'courthouse',
  regulatory_document: 'regulatory',
};

export const isProvisionableKind = (kind: RelatedItemKind): boolean =>
  kind in PROVISIONABLE_KIND_DOCUMENT_TYPES;

// Kinds whose link target IS a Document as their normal state (no separate catalog record).
// Every other provisionable kind targets a Document only transiently — a panel upload
// awaiting auto-connect. Mirrors RelatedItem::DOCUMENT_NATIVE_KINDS on the backend.
export const DOCUMENT_NATIVE_KINDS: RelatedItemKind[] = [
  'courthouse_document',
  'regulatory_document',
];

/** GET {basePath}/related_items envelope. `totalCount` is the filtered total
 * (drives the header count + pager); rows are ordered created_at ASC. */
export interface RelatedItemsListResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  relatedItems: RelatedItem[];
}

export const RELATED_ITEM_KIND_LABELS: Record<RelatedItemKind, string> = {
  note: 'Note',
  uploaded_file: 'Other Document Type / Uploaded File',
  external_link: 'External Link',
  external_title_support: 'External Title Support',
  vesting_document: 'Vesting Document',
  afe: 'AFE',
  division_order: 'Division Order',
  royalty_check: 'Royalty Check',
  courthouse_document: 'Courthouse Document',
  regulatory_document: 'Regulatory Document',
  well: 'Well',
};

export const relatedItemTypeLabel = (doc: RelatedItem): string =>
  RELATED_ITEM_KIND_LABELS[doc.kind] ?? 'Document';

// Display title: the snapshot title, else a file's name, else a note's text.
export const relatedItemTitle = (doc: RelatedItem): string => {
  if (doc.title?.trim()) return doc.title;
  if (doc.kind === 'note') return doc.body?.trim() || 'Note';
  if (doc.kind === 'external_link') {
    return doc.label?.trim() || doc.url?.trim() || 'External link';
  }
  return doc.fileName?.trim() || 'Untitled';
};

// Download visibility is backend-owned via `downloadable` (uploaded files with a
// blob, and record links that snapshotted a file). Never recomputed from kind.
export const documentHasDownload = (doc: RelatedItem): boolean =>
  doc.downloadable === true;

/** Link Document panel dropdown — each option maps 1:1 to a RelatedItemKind. */
export interface RelatedItemTypeOption {
  kind: RelatedItemKind;
  label: string;
  // The host field the option needs; offered only by a host that carries it.
  requires?: HostRequirement;
}

// Host fields a kind needs: a workbook routes under a CRM tract, a vesting search needs its context.
export type HostRequirement = Extract<
  keyof RelatedItemsHost,
  'tractId' | 'vesting'
>;
const KIND_REQUIRES: Partial<Record<RelatedItemKind, HostRequirement>> = {
  external_title_support: 'tractId',
  vesting_document: 'vesting',
};

// Its expanded card owns removal; related_items/:id would address a different record.
const LIST_UNWRITABLE_KINDS = new Set<RelatedItemKind>(['vesting_document']);

export const rowWritable = (doc: RelatedItem): boolean =>
  !LIST_UNWRITABLE_KINDS.has(doc.kind);

// Derived from the labels above so the two never drift. Ordered by kind, not
// label — so "Other Document Type / Uploaded File" sorts under `uploaded_file`.
export const LINK_DOCUMENT_TYPE_OPTIONS: RelatedItemTypeOption[] = (
  Object.keys(RELATED_ITEM_KIND_LABELS) as RelatedItemKind[]
)
  .map((kind) => ({
    kind,
    label: RELATED_ITEM_KIND_LABELS[kind],
    requires: KIND_REQUIRES[kind],
  }))
  .sort((a, b) => a.kind.localeCompare(b.kind));

// Neither kind has a person behind it — a workbook link and a vesting match are both made by a
// job — and an em dash would read as missing data. UAT's wording (Jonathan Welch, sandbox round 1;
// extended to vesting documents in round 2), spelled "Auto-Connected" as everywhere else in Insights.
const AUTO_CONNECTED_KINDS = new Set<RelatedItemKind>([
  'external_title_support',
  'vesting_document',
]);

export const linkedByLabel = (doc: RelatedItem): string =>
  doc.createdBy?.trim() ||
  (AUTO_CONNECTED_KINDS.has(doc.kind) ? 'Auto-Connected' : EMPTY);

// Provenance, not fetch location. Fixed by Mock v2.
export const TITLE_SUPPORT_SOURCES = [
  'Title - Attorney',
  'Title - Landman',
  'County Records',
  'Third-Party Runsheet',
  'Internal File',
] as const;

// Human-owned; re-extraction must not clear these.
export interface TitleSupportDetails {
  source?: string;
  fullRunsheet?: boolean;
  ownership?: boolean;
}

// Backend snapshots `details` as 'Yes'/'No' strings; the panel needs booleans.
export const titleSupportDetailsFrom = (
  doc: RelatedItem
): TitleSupportDetails => {
  const d = (doc.details ?? {}) as Record<string, unknown>;
  const flag = (value: unknown): boolean | undefined => {
    if (value === undefined || value === null) return undefined;
    return value === true || String(value).trim().toLowerCase() === 'yes';
  };

  return {
    source: d.source ? String(d.source) : undefined,
    fullRunsheet: flag(d.fullRunsheet),
    // `coversOwnership`, not `details.ownership`: offer back only human input.
    ownership: flag(doc.coversOwnership),
  };
};
